import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  checkIfMomentReached,
  createMissionMoment,
  MISSION_SCRIPT_KINDS,
} from '../../configs/campaigns/missionFormat';
import {checkIfMomentPassed} from '../../utils/missions/missionBotScript';
import {
  missionAddObjective,
  missionAdvanceScript,
  missionDespawn,
  missionEditorUpdateDraft,
  missionShowReplica,
  missionSpawn,
  setPlayerIsBot,
} from '../../redux/actions';
import {
  selectMissionActiveReplica,
  selectMissionScript,
  selectMissionScriptIndex,
  selectMissionId,
  selectIsMissionEditorMode,
  selectMissionDraft,
} from '../../redux/reducers/privateStateReducer/selectors';
import store from '../../redux/store';

/**
 * Проигрыватель НЕ-действий ленты миссии: реплик, появления заданий и
 * переключений противника на обычного ИИ.
 *
 * Записанные ходы противников воспроизводит движок ботов (см.
 * missionBotScript), но реплика или задание могут выпасть и на ход живого
 * игрока, когда тиков бота нет вовсе — поэтому им нужен отдельный
 * наблюдатель за состоянием партии.
 */
const MissionRunner = () => {
  const dispatch = useDispatch();
  const missionId = useSelector(selectMissionId);
  const script = useSelector(selectMissionScript);
  const scriptIndex = useSelector(selectMissionScriptIndex);
  const activeReplica = useSelector(selectMissionActiveReplica);
  // Момент партии: пересчитываем из тех же полей, что и при записи
  const round = useSelector((state) => state.shared.gameState.round);
  const phase = useSelector((state) => state.shared.gameState.phase);
  const stage = useSelector((state) => state.shared.gameState.warPhaseStage);
  const currentPlayerColor = useSelector((state) => {
    const gameState = state.shared.gameState;
    return gameState.players[gameState.currentPlayerIndex]?.color || null;
  });

  // Снимок поля для конфига миссии делаем ровно в момент старта партии: всё,
  // что автор расставил в фазе подготовки, уедет в mission.field, а дальше
  // пойдёт лента действий
  const isEditorMode = useSelector(selectIsMissionEditorMode);
  const draft = useSelector(selectMissionDraft);
  const isStarted = useSelector((state) => state.shared.gameState.isStarted);
  useEffect(() => {
    if (!isEditorMode || !isStarted || draft?.field) {
      return;
    }
    const state = store.getState();
    const gameState = state.shared.gameState;
    dispatch(missionEditorUpdateDraft({
      field: {
        hexes: state.shared.hexes,
        units: gameState.units,
        specialBuildings: gameState.specialBuildings,
        gridConfig: state.shared.gridConfig,
      },
      // Цвета к старту матча уже переназначены по фракциям, поэтому и цвет
      // живого игрока, и флаги ботов сопоставляем по ИНДЕКСУ состава
      playerColor: gameState.players[draft?.playerIndex ?? 0]?.color || null,
      players: gameState.players.map((player, index) => ({
        ...player,
        isBot: Boolean(draft?.botFlags?.[index]),
      })),
      policyMarket: gameState.policyMarket,
    }));
  }, [isEditorMode, isStarted, draft, dispatch]);

  useEffect(() => {
    if (!missionId || activeReplica) {
      return;
    }
    const entry = script[scriptIndex];
    if (!entry || entry.kind === MISSION_SCRIPT_KINDS.ACTION) {
      return;
    }

    const currentMoment = {round, phase, stage: stage || null, playerColor: currentPlayerColor};
    // Момент мог и не наступить в точности: живой игрок волен сыграть иначе,
    // чем автор в редакторе. Тогда запись выпускаем «вдогонку» — как только
    // партия ушла дальше её раунда, иначе лента встала бы навсегда
    if (!checkIfMomentReached(entry.moment, currentMoment)
      && !checkIfMomentPassed(entry.moment, currentMoment)) {
      return;
    }

    if (entry.kind === MISSION_SCRIPT_KINDS.REPLICA) {
      dispatch(missionShowReplica(entry.replica));
    } else if (entry.kind === MISSION_SCRIPT_KINDS.OBJECTIVE) {
      dispatch(missionAddObjective(entry.objective));
    } else if (entry.kind === MISSION_SCRIPT_KINDS.BOT_AUTO) {
      dispatch(setPlayerIsBot(entry.playerColor, true));
    } else if (entry.kind === MISSION_SCRIPT_KINDS.SPAWN) {
      // Появление стороны посреди миссии: игрок + его отряды (см. редьюсер)
      dispatch(missionSpawn(entry.player, entry.units));
    } else if (entry.kind === MISSION_SCRIPT_KINDS.DESPAWN) {
      dispatch(missionDespawn(entry.playerColor));
    }

    // Индекс обязателен: повторный запуск эффекта (StrictMode) с теми же
    // замкнутыми значениями не должен продвинуть ленту второй раз
    dispatch(missionAdvanceScript(scriptIndex));
  }, [missionId, script, scriptIndex, activeReplica, round, phase, stage, currentPlayerColor, dispatch]);

  return null;
};

export default MissionRunner;

// Пересчёт момента из полного стейта — для мест, где стейт уже под рукой
export const getMissionMoment = createMissionMoment;
